import { useEffect, useState } from "react";
import { CloudOff, RefreshCw, TriangleAlert, Wifi } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ConflictDialog } from "@/components/ConflictDialog";
import {
  cacheNow,
  flushNow,
  initOffline,
  refreshOfflineCounts,
  subscribeOffline,
} from "@/lib/offline/sync";
import { useAuth } from "@/context/AuthContext";

const timeOf = (value) =>
  value ? new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null;

export const OfflineIndicator = () => {
  const { user, workspace } = useAuth();
  const prefs = user?.preferences || {};
  const [state, setState] = useState(null);
  const [conflictsOpen, setConflictsOpen] = useState(false);

  useEffect(() => subscribeOffline(setState), []);

  useEffect(() => {
    if (workspace?.workspace_id) initOffline({ workspaceId: workspace.workspace_id, prefs });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?.workspace_id]);

  if (!state) return null;

  const { online, pending, conflicts, syncing, caching, cachedAt, lastSyncAt, counts } = state;
  const tone = !online
    ? "border-[rgb(var(--c-fixed))] text-[rgb(var(--c-fixed))] bg-[rgb(var(--c-fixed)/0.08)]"
    : conflicts
      ? "border-[rgb(var(--c-fixed))] text-[rgb(var(--c-fixed))]"
      : "border-border text-muted-foreground";
  const label = !online
    ? pending
      ? `Offline · ${pending} waiting`
      : "Offline"
    : syncing || caching
      ? "Syncing…"
      : pending
        ? `${pending} waiting`
        : conflicts
          ? `${conflicts} to review`
          : "Synced";

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <button
            data-testid="offline-indicator"
            data-offline={!online}
            className={`h-10 inline-flex items-center gap-2 px-2.5 sm:px-3 rounded-full border text-xs font-semibold transition-colors duration-200 hover:bg-accent ${tone}`}
            title={online ? "Offline copy" : "Working offline"}
          >
            {!online ? (
              <CloudOff className="h-4 w-4" />
            ) : conflicts ? (
              <TriangleAlert className="h-4 w-4" />
            ) : (
              <Wifi className="h-4 w-4" />
            )}
            <span className="hidden xl:inline" data-testid="offline-indicator-label">
              {label}
            </span>
            {pending ? (
              <span
                data-testid="offline-pending-count"
                className="sm:hidden h-4 min-w-4 px-1 grid place-items-center rounded-full bg-[rgb(var(--c-fixed))] text-white text-[0.6rem]"
              >
                {pending}
              </span>
            ) : null}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80" data-testid="offline-panel">
          <p className="label-caps">{online ? "Offline copy" : "Working offline"}</p>
          <p className="mt-2 text-sm" data-testid="offline-panel-status">
            {online
              ? pending
                ? `${pending} change${pending === 1 ? "" : "s"} waiting to reach the diary.`
                : `Everything is saved to the diary.${lastSyncAt ? ` Last sent ${timeOf(lastSyncAt)}.` : ""}`
              : `The diary is running from this device.${pending ? ` ${pending} change${pending === 1 ? "" : "s"} will be sent when you're back online.` : ""}`}
          </p>

          <dl className="mt-4 grid grid-cols-2 gap-3 text-xs">
            <div>
              <dt className="label-caps">Entries held</dt>
              <dd className="mt-0.5 text-base font-semibold" data-testid="offline-event-count">
                {counts?.events ?? 0}
              </dd>
            </div>
            <div>
              <dt className="label-caps">Matters</dt>
              <dd className="mt-0.5 text-base font-semibold">{counts?.matters ?? 0}</dd>
            </div>
            <div>
              <dt className="label-caps">To-dos</dt>
              <dd className="mt-0.5 text-base font-semibold">{counts?.todos ?? 0}</dd>
            </div>
            <div>
              <dt className="label-caps">Post-its</dt>
              <dd className="mt-0.5 text-base font-semibold">{counts?.postits ?? 0}</dd>
            </div>
          </dl>

          <p className="mt-3 text-xs text-muted-foreground">
            {cachedAt ? `Cached ${new Date(cachedAt).toLocaleString()}` : "Not cached yet"}
          </p>

          {conflicts ? (
            <Button
              data-testid="offline-review-conflicts-btn"
              onClick={() => setConflictsOpen(true)}
              className="mt-4 w-full rounded-full h-10"
            >
              <TriangleAlert className="h-4 w-4 mr-2" /> Review {conflicts} clash
              {conflicts === 1 ? "" : "es"}
            </Button>
          ) : null}

          <div className="mt-4 flex gap-2">
            <Button
              variant="outline"
              data-testid="offline-send-now-btn"
              disabled={!online || syncing || !pending}
              onClick={() => flushNow()}
              className="flex-1 rounded-full h-10 text-xs"
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} /> Send now
            </Button>
            <Button
              variant="outline"
              data-testid="offline-cache-now-btn"
              disabled={!online || caching}
              onClick={() => cacheNow(prefs)}
              className="flex-1 rounded-full h-10 text-xs"
            >
              {caching ? "Caching…" : "Cache now"}
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <ConflictDialog
        open={conflictsOpen}
        onOpenChange={(v) => {
          setConflictsOpen(v);
          if (!v) refreshOfflineCounts();
        }}
      />
    </>
  );
};
